import { useCallback, useState } from 'react';

import { MANUAL_PICKING_MODE_AVAILABLE } from 'lib/selectors/getPickingFieldConfig';
import { throwSuccessToast } from 'lib/utils/toast';

const STORAGE_KEY = 'materialPicking.manualMode';

const readStored = () => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
};

const writeStored = (on) => {
  try {
    if (on) {
      window.sessionStorage.setItem(STORAGE_KEY, 'on');
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage blocked: the mode still applies until the wizard unmounts.
  }
};

/**
 * Manual mode for material picking: license plates can be selected by tapping
 * their tile as well as by scanning. Picking is scan-only otherwise.
 *
 * It's hidden: press and hold the pick step's title to toggle it. It's only
 * available in development and staging builds (MANUAL_PICKING_MODE_AVAILABLE),
 * and it lasts for the browser session, so a reload turns it off.
 *
 * @returns {{ manualMode: boolean, toggleManualMode: (Function|undefined) }}
 *   toggleManualMode is undefined where manual mode is not available.
 */
const useManualPickingMode = () => {
  const [manualMode, setManualMode] = useState(() => {
    return MANUAL_PICKING_MODE_AVAILABLE && readStored();
  });

  const toggleManualMode = useCallback(() => {
    const next = !manualMode;
    writeStored(next);
    setManualMode(next);
    throwSuccessToast(next ? 'Manual mode on' : 'Manual mode off');
  }, [manualMode]);

  return {
    manualMode,
    toggleManualMode: MANUAL_PICKING_MODE_AVAILABLE
      ? toggleManualMode
      : undefined,
  };
};

export default useManualPickingMode;
